import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, AccordionSummaryOwnProps, Box, SxProps, Theme } from "@mui/material";
import { IconCaret } from "src/components/icons-mui/caret";
import { MUI_NAV_HEIGHT } from "src/components/layout/default-layout";
import { Typography } from "src/components-mui/base/typography/typography";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { isServerSide } from "src/utils/common-util";
import { getDecodedUrlHash } from "src/utils/url/url";

export interface IWfAccordionItem {
    label: string | ReactElement;
    value: string | ReactElement;
    anchor?: string;
}

export const NO_EXPANDED_ACCORDION_INDEX = -1;

export interface IWfAccordionProps {
    data: IWfAccordionItem[];
    shouldScrollIntoView?: boolean;
    variant?: AccordionSummaryOwnProps["variant"];
    allowMultiple?: boolean;
    expandedIndex?: number;
    onChange?: (expandedIndex: number) => void;
    sx?: SxProps<Theme>;
}

interface IAccordionItemWrapperProps {
    item: IWfAccordionItem;
    shouldScrollIntoView: boolean;
    expanded?: boolean;
    onChange?: () => void;
    variant?: AccordionSummaryOwnProps["variant"];
}

const ADJUSTED_NAV_HEIGHT = {
    ...MUI_NAV_HEIGHT,
    xs: MUI_NAV_HEIGHT.xs + 8,
    sm: MUI_NAV_HEIGHT.sm + 8,
    md: MUI_NAV_HEIGHT.md + 8,
};

const AccordionItemWrapper = ({ item, expanded, onChange, variant, shouldScrollIntoView }: IAccordionItemWrapperProps) => {
    const ref = useRef<HTMLDivElement | null>(null);

    const scrollIntoView = useCallback(() => {
        setTimeout(() => {
            const rect = ref.current?.getBoundingClientRect();
            const SCROLL_TOP_MARGIN = 70;
            const outOfViewport = rect && (rect.top + rect.height > window.innerHeight - SCROLL_TOP_MARGIN || rect.top < SCROLL_TOP_MARGIN);
            if (outOfViewport) {
                ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 150);
    }, []);

    useEffect(() => {
        if (isServerSide() || !item.anchor || !window.location.hash) {
            return;
        }

        if (item.anchor === getDecodedUrlHash()) {
            setTimeout(() => ref.current?.querySelector("button")?.click(), 100);
        }
    }, [item.anchor, scrollIntoView]);

    const handleChange = () => {
        if (!expanded && shouldScrollIntoView) {
            scrollIntoView();
        }

        onChange?.();
    };

    return (
        <Accordion
            expanded={expanded}
            sx={{ scrollMarginTop: ADJUSTED_NAV_HEIGHT }}
            ref={ref}
            onChange={handleChange}
            slotProps={{ transition: { timeout: 200 } }}
        >
            <AccordionSummary expandIcon={<IconCaret />} variant={variant}>
                <Typography
                    component="div"
                    sx={{
                        width: "100%",
                        fontSize: "inherit",
                        fontWeight: "semibold",
                    }}
                >
                    {item.label}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>{typeof item.value === "string" ? <SimpleRichText text={item.value} /> : item.value}</AccordionDetails>
        </Accordion>
    );
};

export const WfAccordion = ({
    data,
    variant,
    shouldScrollIntoView = false,
    allowMultiple = false,
    expandedIndex,
    onChange,
    sx,
}: IWfAccordionProps) => {
    const [uncontrolledExpandedIndexes, setUncontrolledExpandedIndexes] = useState<number[]>([]);
    const isControlled = expandedIndex !== undefined;
    const expandedIndexes = isControlled ? [expandedIndex] : uncontrolledExpandedIndexes;

    const handleToggle = (index: number) => {
        const isExpanded = expandedIndexes.includes(index);

        if (!isControlled) {
            const otherExpandedIndexes = allowMultiple ? expandedIndexes.filter(expandedItemIndex => expandedItemIndex !== index) : [];
            setUncontrolledExpandedIndexes(isExpanded ? otherExpandedIndexes : [...otherExpandedIndexes, index]);
        }

        onChange?.(isExpanded ? NO_EXPANDED_ACCORDION_INDEX : index);
    };

    return (
        <Box sx={sx}>
            {data.map((item, index) => (
                <AccordionItemWrapper
                    expanded={expandedIndexes.includes(index)}
                    key={index}
                    item={item}
                    shouldScrollIntoView={shouldScrollIntoView}
                    onChange={() => handleToggle(index)}
                    variant={variant}
                />
            ))}
        </Box>
    );
};
